import axios from 'axios';

/**
 * Save resilience helpers (idempotent create + honest timeout UX).
 *
 * See .claude/save-idempotency/DESIGN.md (Part A). Pure helpers only: no React, no store.
 */

/** Per-request timeout for post create / update / draft-save calls (the global axios default stays 10s). */
export const SAVE_REQUEST_TIMEOUT_MS = 30_000;

/** Poll schedule for GET create-requests/:key after an unconfirmed create (about 29s in total). */
export const CREATE_LOOKUP_DELAYS_MS: readonly number[] = [2_000, 4_000, 8_000, 15_000];

/** Poll schedule for verifying an unconfirmed update through GET post. */
export const UPDATE_VERIFY_DELAYS_MS: readonly number[] = [2_000, 4_000, 8_000];

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when `value` is a canonical UUID string. */
export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/**
 * Generate a v4 UUID. `crypto.randomUUID` is missing on insecure contexts (plain http on a LAN,
 * old WebViews), so fall back to getRandomValues, then to Math.random as a last resort.
 */
export function generateCreateIntentKey(): string {
  const cryptoApi: Crypto | undefined = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Timeout / dropped connection / gateway error: the request may or may not have been applied. */
export function isTransientSaveError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.code === 'ERR_CANCELED') return false;
  if (!error.response) return true; // ECONNABORTED, ETIMEDOUT, ERR_NETWORK, ...
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
}

/** 409 `save_in_progress`: the same key is still being processed by the server. */
export function isSaveInProgressConflict(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 409) return false;
  const data: unknown = error.response.data;
  return typeof data === 'object' && data !== null && (data as Record<string, unknown>).code === 'save_in_progress';
}

/** True for a timeout (as opposed to a generic network failure). */
export function isTimeoutError(error: unknown): boolean {
  return axios.isAxiosError(error) && (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT');
}

/** True for a request that never got an HTTP response and is not a timeout. */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response && !isTimeoutError(error) && error.code !== 'ERR_CANCELED';
}

/**
 * Thrown by a replayed (lookup=completed) create whose ORIGINAL outcome was an error after the post
 * had been committed (schedule step failed). Carries the committed post id so the composer adopts it.
 */
export class ReplayedCreateError extends Error {
  readonly createdPostId: number | null;
  /** Backend error code of the stored error body (e.g. 'partial_create'), when present. */
  readonly code: string | null;

  constructor(message: string, createdPostId: number | null, code: string | null = null) {
    super(message);
    this.name = 'ReplayedCreateError';
    this.createdPostId = createdPostId;
    this.code = code;
  }
}

/**
 * Thrown after the save-resilience UI state was already set (not saved / still saving). Callers must
 * swallow it silently: no toast, no error banner.
 */
export class SaveNotConfirmedError extends Error {
  readonly reason: 'not_saved' | 'still_saving';

  constructor(reason: 'not_saved' | 'still_saving') {
    super(reason);
    this.name = 'SaveNotConfirmedError';
    this.reason = reason;
  }
}

/** Abortable sleep. Resolves true when the full delay elapsed, false when `isCancelled` flipped. */
export async function sleepUnlessCancelled(ms: number, isCancelled: () => boolean): Promise<boolean> {
  const step = 250;
  let waited = 0;
  while (waited < ms) {
    if (isCancelled()) return false;
    const slice = Math.min(step, ms - waited);
    await new Promise<void>((resolve) => setTimeout(resolve, slice));
    waited += slice;
  }
  return !isCancelled();
}

/** Small, stable, non-cryptographic string hash (djb2) used to pair a persisted key with ITS draft. */
export function hashString(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(16);
}

/** 422 `idempotency_body_mismatch`: the key was already used for a DIFFERENT request body. */
export function isIdempotencyBodyMismatch(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status !== 422) return false;
  const data: unknown = error.response.data;
  return typeof data === 'object' && data !== null && (data as Record<string, unknown>).code === 'idempotency_body_mismatch';
}

/** sessionStorage slot that keeps an unconfirmed create-intent key across a reload. */
function intentStorageKey(teamId: number | null | undefined): string {
  return `aa:create-intent:${teamId ?? 'none'}`;
}

/**
 * The key is stored together with a hash of the draft content it was issued for, and re-attached on
 * restore ONLY to that exact draft (never to an arbitrary one).
 */
export function readPersistedIntentKey(teamId: number | null | undefined, draftHash: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(intentStorageKey(teamId));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    return isUuid(record.key) && record.draftHash === draftHash ? record.key : null;
  } catch {
    return null;
  }
}

export function persistIntentKey(teamId: number | null | undefined, key: string, draftHash: string): void {
  try {
    window.sessionStorage.setItem(intentStorageKey(teamId), JSON.stringify({ key, draftHash }));
  } catch {
    // Storage unavailable (private mode / quota): the in-memory key still protects this session.
  }
}

export function clearPersistedIntentKey(teamId: number | null | undefined): void {
  try {
    window.sessionStorage.removeItem(intentStorageKey(teamId));
  } catch {
    // Nothing to clear.
  }
}
